import { useEffect, useState } from 'react';

/**
 * Just Be Founders — the in-retreat decision page.
 *
 * Hidden / invitation-only. Reachable at:
 *   /just-be-founders
 *
 * Curated specifically for women attending the Just Be: Soft Power™ Retreat
 * in Costa Rica. The page is structured as an emotional storyline that ends
 * at the pricing reveal — the opposite order of the public Legacy Circle page.
 *
 *   Hero → What Usually Happens Next → Become Her / Stay Her →
 *   Imagine Looking Back → One Testimonial → What's Included (6 icons) →
 *   Pricing → Fine Print.
 */

const FULL_PAY_URL = 'https://support.sisterdiamonds.com/legacycirclefullpay';
const PLAN_URL = 'https://support.sisterdiamonds.com/legacycirclepaymentplan';

const HERO_IMG = '/legacy/hero-jungle-circle.jpg';
const BECOME_IMG = '/legacy/woman-portrait.jpeg';
const PROBLEM_IMG = '/legacy/section2-journaling.jpg';

const SERIF = { fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" };
const SANS = { fontFamily: "'Manrope', system-ui, sans-serif" };

const IVORY = '#FBF7F0';
const SOFT = '#F5EFE3';
const CHAMP = '#E9D9B7';
const GOLD = '#B08A45';
const TAUPE = '#8A7A66';
const BROWN = '#3A2E22';

// Feature flag — set to `true` to re-enable pricing/purchase links on the page.
// Direct Ontraport order forms (FULL_PAY_URL / PLAN_URL) remain fully functional
// and reachable via their direct URLs regardless of this flag.
const SHOW_PRICING = false;

// Founding-Member pricing deadline: midnight at the END of July 1, 2026 (America/New_York).
// July is EDT (UTC-4) → 2026-07-02 04:00 UTC.
const DEADLINE_UTC = new Date(Date.UTC(2026, 6, 2, 4, 0, 0));

const CountdownTimer = ({ deadline }) => {
  const compute = () => {
    const diff = Math.max(0, deadline.getTime() - Date.now());
    const total = Math.floor(diff / 1000);
    return {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
      expired: diff === 0,
    };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (t.expired) {
    return (
      <div
        className="mx-auto max-w-2xl text-center px-6 py-6 mb-12"
        style={{ background: IVORY, border: `1px solid ${CHAMP}` }}
        data-testid="jb-countdown-expired"
      >
        <p
          style={{ ...SANS, color: GOLD, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
        >
          Founding Member Pricing
        </p>
        <p
          className="mt-3"
          style={{ ...SERIF, color: BROWN, fontSize: '1.5rem', fontStyle: 'italic' }}
        >
          Has closed.
        </p>
      </div>
    );
  }

  const pad = (n) => String(n).padStart(2, '0');
  const cells = [
    { label: 'Days', value: pad(t.days) },
    { label: 'Hours', value: pad(t.hours) },
    { label: 'Minutes', value: pad(t.minutes) },
    { label: 'Seconds', value: pad(t.seconds) },
  ];

  return (
    <div className="mx-auto max-w-2xl mb-12" data-testid="jb-countdown">
      <p
        className="text-center mb-5"
        style={{ ...SANS, color: GOLD, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
      >
        Founding Member Pricing Closes
      </p>
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        {cells.map(({ label, value }) => (
          <div
            key={label}
            className="text-center px-2 py-5 sm:py-6"
            style={{ background: IVORY, border: `1px solid ${CHAMP}` }}
            data-testid={`jb-countdown-${label.toLowerCase()}`}
          >
            <p
              style={{
                ...SERIF,
                color: BROWN,
                fontSize: 'clamp(1.85rem, 6vw, 2.75rem)',
                lineHeight: 1,
                fontWeight: 400,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em',
              }}
            >
              {value}
            </p>
            <p
              className="mt-3"
              style={{ ...SANS, color: TAUPE, fontSize: '10px', letterSpacing: '0.28em', textTransform: 'uppercase' }}
            >
              {label}
            </p>
          </div>
        ))}
      </div>
      <p
        className="text-center mt-5"
        style={{ ...SERIF, color: TAUPE, fontSize: '0.95rem', fontStyle: 'italic' }}
      >
        Midnight EST · July&nbsp;1, 2026
      </p>
    </div>
  );
};

const JustBeFoundersPage = () => {
  useEffect(() => {
    document.title = 'For Just Be Founders — Sister Diamonds';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    const prev = robots.content;
    robots.content = 'noindex, nofollow';
    return () => {
      robots.content = prev || '';
    };
  }, []);

  return (
    <div style={{ background: IVORY, color: BROWN }} data-testid="jb-founders-page">
      {/* ============================================================
          HERO — The emotional headline (no pricing here)
          ============================================================ */}
      <section className="relative" style={{ background: IVORY }} data-testid="jb-hero">
        {/* Mobile-only header above the image */}
        <div className="lg:hidden px-6 sm:px-12 pt-12 pb-6">
          <p
            className="text-[10px] uppercase mb-5"
            style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
          >
            Legacy Circle · Founding Cohort 2026–2027 · By Invitation Only
          </p>
          <h1
            className="text-[26px] sm:text-4xl leading-[1.08]"
            style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.015em' }}
          >
            Don&rsquo;t Let Costa Rica Become Just Another{' '}
            <span style={{ fontStyle: 'italic', color: GOLD }}>Beautiful Memory.</span>
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 items-stretch">
          {/* LEFT — Editorial text panel (mobile: 2nd; desktop: 1st) */}
          <div
            className="lg:col-span-6 lg:order-1 order-2 px-6 sm:px-12 lg:pl-20 lg:pr-16 py-8 sm:py-12 lg:py-24 flex flex-col justify-center"
            style={{ color: BROWN }}
          >
            <p
              className="hidden lg:block text-sm uppercase mb-8"
              style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
              data-testid="jb-eyebrow"
            >
              Legacy Circle · Founding Cohort 2026–2027 · By Invitation Only
            </p>

            <h1
              className="hidden lg:block lg:text-5xl xl:text-6xl leading-[1.08] max-w-xl"
              style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.015em' }}
              data-testid="jb-headline"
            >
              Don&rsquo;t Let Costa Rica Become Just Another{' '}
              <span style={{ fontStyle: 'italic', color: GOLD }}>Beautiful Memory.</span>
            </h1>

            <div className="hidden lg:block my-7" style={{ width: '4rem', height: '1px', background: GOLD }} />

            <p
              className="max-w-xl text-lg sm:text-xl leading-relaxed"
              style={{ ...SERIF, color: BROWN, fontWeight: 400 }}
              data-testid="jb-subheadline"
            >
              Something meaningful happened here.
            </p>
            <p
              className="mt-3 max-w-xl text-lg sm:text-xl leading-relaxed"
              style={{ ...SERIF, color: BROWN, fontWeight: 400 }}
            >
              Legacy Circle is where you continue becoming the woman you know
              you&rsquo;re meant to be.
            </p>
          </div>

          {/* RIGHT — Image (mobile: 1st; desktop: 2nd) */}
          <div
            className="lg:col-span-6 lg:order-2 order-1 relative overflow-hidden flex flex-col items-stretch justify-center"
            style={{ background: IVORY, minHeight: '320px' }}
          >
            <img
              src={HERO_IMG}
              alt="Just Be: Soft Power retreat sisters gathered in a Costa Rica jungle circle"
              className="w-full h-auto lg:absolute lg:inset-0 lg:h-full"
              style={{ objectFit: 'contain', objectPosition: 'center' }}
            />
            <div
              className="px-6 sm:px-12 lg:px-10 pt-5 pb-8 sm:pb-10
                         lg:absolute lg:bottom-6 lg:left-6 lg:right-6 lg:pt-6 lg:pointer-events-none"
              style={{
                background: 'rgba(251, 247, 240, 0.94)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                border: `1px solid ${CHAMP}`,
                boxShadow: '0 6px 24px rgba(58, 46, 34, 0.10)',
              }}
              data-testid="jb-hero-caption"
            >
              <div className="mb-3" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.85 }} />
              <p
                className="text-xl sm:text-2xl lg:text-[1.65rem] leading-[1.2] max-w-xl"
                style={{ ...SERIF, color: BROWN, fontWeight: 500, letterSpacing: '-0.005em' }}
              >
                A Community Full of Women Who{' '}
                <span style={{ fontStyle: 'italic', color: GOLD }}>
                  Refuse To Let You Abandon Yourself.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          THE PROBLEM
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-20 sm:py-28"
        style={{ background: IVORY }}
        data-testid="jb-problem"
      >
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Copy */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <p
                className="text-xs sm:text-sm uppercase mb-6"
                style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
              >
                What Usually Happens Next
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl leading-tight"
                style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
              >
                Most women leave transformational experiences{' '}
                <em style={{ color: GOLD, fontStyle: 'italic' }}>inspired</em>.
              </h2>
              <div className="my-8" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.6 }} />
              <div className="space-y-5 text-lg sm:text-xl leading-relaxed" style={{ ...SERIF, color: TAUPE }}>
                <p>Then life happens.</p>
                <p>Work returns.</p>
                <p>Family needs you.</p>
                <p>Old habits quietly reappear.</p>
                <p style={{ color: BROWN, fontStyle: 'italic' }}>
                  Six months later, the breakthrough has become a beautiful memory.
                </p>
              </div>
              <p
                className="mt-10 text-2xl sm:text-3xl leading-snug"
                style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.005em' }}
              >
                Legacy Circle exists so{' '}
                <em style={{ color: GOLD, fontStyle: 'italic' }}>that doesn&rsquo;t happen to you.</em>
              </p>
            </div>

            {/* Editorial portrait */}
            <div className="lg:col-span-5 order-1 lg:order-2 max-w-sm mx-auto lg:max-w-none w-full">
              <div
                className="relative overflow-hidden"
                style={{ aspectRatio: '4 / 5', border: `1px solid ${CHAMP}` }}
              >
                <img
                  src={PROBLEM_IMG}
                  alt="Two retreat sisters journaling and talking together on a Costa Rica porch"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  loading="lazy"
                  data-testid="jb-problem-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          THE NORTH STAR — the line that captures everything
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32 text-center"
        style={{ background: BROWN, color: IVORY }}
        data-testid="jb-north-star"
      >
        <div className="mx-auto max-w-4xl">
          <p
            className="text-xs sm:text-sm uppercase mb-6"
            style={{ ...SANS, color: CHAMP, letterSpacing: '0.32em', fontWeight: 600 }}
          >
            Why Legacy Circle Exists
          </p>
          <h2
            className="text-3xl sm:text-5xl lg:text-6xl leading-[1.1]"
            style={{ ...SERIF, color: IVORY, fontWeight: 400, letterSpacing: '-0.01em' }}
          >
            The retreat helped you{' '}
            <em style={{ color: CHAMP, fontStyle: 'italic' }}>become her.</em>
            <br />
            Legacy Circle helps you{' '}
            <em style={{ color: CHAMP, fontStyle: 'italic' }}>stay her.</em>
          </h2>
        </div>
      </section>

      {/* ============================================================
          ONE YEAR FROM TODAY — visual checkmark list anchored to retreat
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        style={{ background: IVORY }}
        data-testid="jb-future"
      >
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Portrait */}
            <div className="lg:col-span-5 max-w-sm mx-auto lg:max-w-none w-full">
              <div
                className="relative overflow-hidden"
                style={{ aspectRatio: '4 / 5', border: `1px solid ${CHAMP}` }}
              >
                <img
                  src={BECOME_IMG}
                  alt="A confident, joyful woman — one year from today"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  loading="lazy"
                />
              </div>
              <p
                className="mt-5 text-sm italic"
                style={{ ...SERIF, color: TAUPE, letterSpacing: '0.01em' }}
              >
                — One year from today.
              </p>
            </div>

            {/* Outcomes */}
            <div className="lg:col-span-7">
              <p
                className="text-xs sm:text-sm uppercase mb-6"
                style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
              >
                Imagine Looking Back
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl leading-tight"
                style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
              >
                One Year From Today{' '}
                <em style={{ color: GOLD, fontStyle: 'italic' }}>…and realizing.</em>
              </h2>
              <div className="my-8" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.6 }} />

              <ul className="space-y-4">
                {[
                  'You never slipped back into survival mode.',
                  'You kept the promises you made to yourself in Costa Rica.',
                  'You stopped carrying everything alone.',
                  'Your boundaries became stronger.',
                  'Your life finally reflected the woman you met this week.',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span
                      className="mt-1 flex-shrink-0 text-base sm:text-lg"
                      style={{ color: GOLD, fontFamily: 'serif', lineHeight: 1 }}
                    >
                      ✔
                    </span>
                    <p style={{ ...SERIF, color: BROWN, fontSize: '1.1rem', lineHeight: 1.5 }}>
                      {line}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TESTIMONIAL — CeWyon
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        style={{ background: SOFT }}
        data-testid="jb-testimonial"
      >
        <div className="mx-auto max-w-4xl text-center">
          <p
            className="text-xs sm:text-sm uppercase mb-6"
            style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
          >
            From a Sister Diamond
          </p>
          <figure
            className="mt-2"
            style={{ borderTop: `1px solid ${CHAMP}`, borderBottom: `1px solid ${CHAMP}` }}
          >
            <blockquote
              className="py-12 px-4 text-xl sm:text-2xl lg:text-3xl leading-relaxed"
              style={{ ...SERIF, color: BROWN, fontStyle: 'italic', fontWeight: 400 }}
            >
              &ldquo;Sister Diamonds gave me the space to breathe, reflect, and
              strategize. The community is unlike anything I&rsquo;ve experienced.&rdquo;
            </blockquote>
          </figure>
          <figcaption className="mt-6" style={{ ...SANS }}>
            <p style={{ color: BROWN, fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.04em' }}>
              CeWyon Chandler-Ward
            </p>
            <p style={{ color: TAUPE, fontSize: '0.75rem', letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: '4px' }}>
              Serial Entrepreneur · Angel Investor
            </p>
          </figcaption>
        </div>
      </section>

      {/* ============================================================
          WHAT'S INCLUDED — emotional support framing (not a calendar)
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32 relative overflow-hidden"
        style={{ background: SOFT }}
        data-testid="jb-included"
      >
        {/* Decorative serif backdrop numeral */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute select-none pointer-events-none"
          style={{
            ...SERIF,
            top: '4rem',
            right: '-1.5rem',
            fontSize: '22rem',
            lineHeight: 1,
            color: CHAMP,
            opacity: 0.18,
            fontStyle: 'italic',
            letterSpacing: '-0.04em',
          }}
        >
          VI
        </div>

        <div className="mx-auto max-w-5xl relative">
          <div className="max-w-3xl">
            <p
              className="text-xs sm:text-sm uppercase mb-6"
              style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
            >
              Here&rsquo;s How We&rsquo;ll Hold You Throughout The Year
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl leading-tight"
              style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
            >
              This isn&rsquo;t more to do.
              <br />
              It&rsquo;s{' '}
              <em style={{ color: GOLD, fontStyle: 'italic' }}>more support</em>{' '}
              for the woman you&rsquo;re becoming.
            </h2>
            <div className="my-8" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.7 }} />
          </div>

          <div className="grid sm:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-6 mt-12">
            {[
              {
                title: 'A Place to Exhale',
                body: 'Monthly gatherings that help you slow down, reflect, and reconnect with yourself.',
              },
              {
                title: 'Women Who Walk Beside You',
                body: "A small circle of sisters who know your story and won't let you carry it alone.",
              },
              {
                title: 'Seasonal Renewal Gatherings',
                body: 'Gentle pauses throughout the year to realign before life pulls you too far off course.',
              },
              {
                title: 'Support Between Gatherings',
                body: 'A private community where encouragement, wisdom, celebration, and prayer are always within reach.',
              },
              {
                title: 'A Place to Return',
                body: 'Priority access and preferred pricing for future Sister Diamonds experiences.',
              },
              {
                title: 'Founding Member Legacy',
                body: 'Recognition as one of the women who helped shape the inaugural Legacy Circle.',
              },
            ].map(({ title, body }, i) => {
              const num = String(i + 1).padStart(2, '0');
              return (
                <div
                  key={title}
                  data-testid={`jb-included-${title.replace(/\s+/g, '-').toLowerCase()}`}
                  className="group relative p-7 sm:p-8 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    background: IVORY,
                    border: `1px solid ${CHAMP}`,
                    boxShadow: '0 1px 0 rgba(58, 46, 34, 0.04)',
                  }}
                >
                  {/* Serif numeral */}
                  <span
                    style={{
                      ...SERIF,
                      color: GOLD,
                      fontSize: '2.25rem',
                      lineHeight: 1,
                      fontStyle: 'italic',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {num}
                  </span>

                  {/* Hairline */}
                  <div
                    className="transition-all duration-500 group-hover:w-14 mt-4"
                    style={{ width: '1.75rem', height: '1px', background: GOLD, opacity: 0.65, marginBottom: '1rem' }}
                  />

                  {/* Title */}
                  <h3
                    style={{
                      ...SERIF,
                      color: BROWN,
                      fontSize: '1.4rem',
                      lineHeight: 1.25,
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      marginBottom: '0.6rem',
                    }}
                  >
                    {title}
                  </h3>

                  {/* Body */}
                  <p
                    style={{
                      ...SERIF,
                      color: TAUPE,
                      fontSize: '1.02rem',
                      lineHeight: 1.55,
                      fontStyle: 'italic',
                      fontWeight: 400,
                    }}
                  >
                    {body}
                  </p>

                  {/* Corner diamond accent */}
                  <span
                    aria-hidden="true"
                    className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      bottom: '0.75rem',
                      right: '0.75rem',
                      width: '0.35rem',
                      height: '0.35rem',
                      background: GOLD,
                      transform: 'rotate(45deg)',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          INVESTMENT — simplified Founding Member pricing
          (Hidden via SHOW_PRICING flag — Ontraport URLs remain valid)
          ============================================================ */}
      {SHOW_PRICING && (
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        style={{ background: IVORY }}
        data-testid="jb-investment"
      >
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p
              className="text-xs sm:text-sm uppercase mb-6"
              style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
            >
              Founding Member Investment
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto"
              style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
            >
              Available only{' '}
              <em style={{ color: GOLD, fontStyle: 'italic' }}>before leaving Costa Rica.</em>
            </h2>
            <div className="mx-auto my-8" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.6 }} />
          </div>

          <CountdownTimer deadline={DEADLINE_UTC} />

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-10 max-w-4xl mx-auto">
            {/* PAID IN FULL */}
            <div
              className="p-8 sm:p-10 flex flex-col"
              style={{ background: BROWN, color: IVORY }}
              data-testid="jb-card-fullpay"
            >
              <p
                className="mb-6"
                style={{ ...SANS, color: CHAMP, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Pay in Full
              </p>
              <p
                style={{ ...SERIF, color: IVORY, fontSize: '3.5rem', lineHeight: 1, fontWeight: 400 }}
              >
                $10,000
              </p>
              <p
                className="mt-3 mb-10"
                style={{ ...SANS, color: CHAMP, fontSize: '0.95rem', letterSpacing: '0.04em', fontStyle: 'italic' }}
              >
                (Save $2,000)
              </p>
              <a
                href={FULL_PAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="jb-cta-fullpay"
                className="mt-auto inline-flex items-center justify-center w-full px-8 py-4 transition-all duration-500 hover:tracking-[0.28em]"
                style={{
                  ...SANS,
                  background: IVORY,
                  color: BROWN,
                  letterSpacing: '0.22em',
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  border: `1px solid ${IVORY}`,
                }}
              >
                Become a Founding Member
              </a>
            </div>

            {/* PAYMENT PLAN */}
            <div
              className="p-8 sm:p-10 flex flex-col"
              style={{ background: 'transparent', color: BROWN, border: `1px solid ${BROWN}` }}
              data-testid="jb-card-plan"
            >
              <p
                className="mb-6"
                style={{ ...SANS, color: GOLD, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Payment Plan
              </p>
              <p
                style={{ ...SERIF, color: BROWN, fontSize: '3.5rem', lineHeight: 1, fontWeight: 400 }}
              >
                $917
                <span style={{ ...SERIF, color: TAUPE, fontSize: '1.25rem', fontStyle: 'italic' }}>
                  {' '}/ month
                </span>
              </p>
              <p
                className="mt-3"
                style={{ ...SANS, color: TAUPE, fontSize: '0.95rem', letterSpacing: '0.04em' }}
              >
                12 months
              </p>
              <p
                className="mb-10"
                style={{ ...SANS, color: TAUPE, fontSize: '0.95rem', letterSpacing: '0.04em', fontStyle: 'italic' }}
              >
                (Save $996)
              </p>
              <a
                href={PLAN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="jb-cta-plan"
                className="mt-auto inline-flex items-center justify-center w-full px-8 py-4 transition-all duration-500 hover:tracking-[0.28em]"
                style={{
                  ...SANS,
                  background: BROWN,
                  color: IVORY,
                  letterSpacing: '0.22em',
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  border: `1px solid ${BROWN}`,
                }}
              >
                Become a Founding Member
              </a>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============================================================
          FOOTER — slim legal links
          ============================================================ */}
      <footer
        className="px-6 sm:px-10 lg:px-16 py-10"
        style={{ background: SOFT, color: BROWN, borderTop: `1px solid ${CHAMP}` }}
        data-testid="jb-footer"
      >
        <div className="mx-auto max-w-5xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p
            style={{ ...SANS, color: TAUPE, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            © Sister Diamonds · Legacy Circle Founding Cohort 2026–2027
          </p>
          <nav
            className="flex flex-wrap gap-x-6 gap-y-2"
            style={{ ...SANS, fontSize: '12px', letterSpacing: '0.08em' }}
            aria-label="Legal documents"
          >
            <a
              href="/legacy/Legacy-Circle-Enrollment-Agreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="jb-terms-enrollment"
              className="transition-colors duration-300 hover:opacity-100"
              style={{ color: BROWN, textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationColor: CHAMP, opacity: 0.85 }}
            >
              Enrollment Agreement
            </a>
            <a
              href="/legacy/Volition-Master-Terms-and-Privacy.docx"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="jb-terms-master"
              className="transition-colors duration-300 hover:opacity-100"
              style={{ color: BROWN, textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationColor: CHAMP, opacity: 0.85 }}
            >
              Terms of Service &amp; Privacy
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default JustBeFoundersPage;
